import { useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { TherapistWithSpecialties, Session } from "@shared/schema";

// Interface para sessão estendida com dados do terapeuta
interface SessionExtended extends Session {
  therapist: TherapistWithSpecialties;
}
import Header from "@/components/layout/header";
import Footer from "@/components/layout/footer";
import TherapistCard from "@/components/therapist-card";
import { Button } from "@/components/ui/button";
import { Loader2, CalendarCheck, Clock, History } from "lucide-react";

export default function HomePage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();

  // Redirect to auth if not logged in
  useEffect(() => {
    console.log(user)
    if (!user) {
      navigate("/auth");
    }
  }, [user, navigate]);

  // Fetch therapists
  const { data: therapists, isLoading: therapistsLoading } = useQuery<TherapistWithSpecialties[]>({
    queryKey: ["/api/therapists"],
    staleTime: 60000, // 1 minute
  });

  // Fetch free session status
  const { data: freeSessionStatus, isLoading: statusLoading } = useQuery<{ freeSessionAvailable: boolean, resetDate: string }>({
    queryKey: ["/api/free-session-status"],
    staleTime: 60000, // 1 minute
  });

  // Fetch user sessions
  const { data: sessions, isLoading: sessionsLoading } = useQuery<SessionExtended[]>({
    queryKey: ["/api/sessions"],
    staleTime: 60000, // 1 minute
  });

  if (!user) return null;
  
  const getFirstName = (fullName: string) => {
    return fullName.split(" ")[0];
  };

  const getUpcomingSession = (): SessionExtended | null => {
    if (!sessions || !Array.isArray(sessions)) return null;
    const now = new Date();
    
    return sessions
      .filter((session: SessionExtended) => new Date(session.date) > now && session.status === "scheduled")
      .sort((a: SessionExtended, b: SessionExtended) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
  };

  const upcomingSession = getUpcomingSession();
  
  const formatDate = (dateInput: string | Date) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return "Data inválida";
    }
    return new Intl.DateTimeFormat('pt-BR', { 
      day: '2-digit', 
      month: '2-digit' 
    }).format(date);
  };
  
  const formatTime = (dateInput: string | Date) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return "Horário inválido";
    }
    return new Intl.DateTimeFormat('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    }).format(date);
  };

  const completedSessionsCount = sessions && Array.isArray(sessions)
    ? sessions.filter((session: SessionExtended) => session.status === "completed").length
    : 0;

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-grow">
        <section className="container mx-auto px-4 py-8 md:py-12">
          {/* Dashboard Section */}
          <div className="bg-white shadow-sm rounded-lg p-6 md:p-8 mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold">
                  Olá, {getFirstName(user.firstName)}!
                </h2>
                <p className="text-gray-600 mt-1">Bem-vindo(a) de volta ao seu espaço de bem-estar</p>
              </div>
              <div className="mt-4 md:mt-0">
                <Link href="/booking">
                  <Button className="bg-primary hover:bg-secondary text-white rounded-full">
                    Agendar Sessão
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div className="bg-accent rounded-lg p-5 md:p-6">
                <div className="flex items-start">
                  <div className="rounded-full bg-primary/20 p-3 mr-4">
                    <CalendarCheck className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Sessão Mensal Gratuita</h3>
                    <p className="text-gray-600 mt-1 text-sm">
                      {statusLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <span>
                            {freeSessionStatus?.freeSessionAvailable ? "Disponível" : "Indisponível"}
                          </span>
                          {" "}para este mês
                        </>
                      )}
                    </p>
                    {freeSessionStatus?.freeSessionAvailable && (
                      <Link href="/booking" className="text-primary hover:text-secondary text-sm font-medium mt-2 inline-block">
                        Agendar agora →
                      </Link>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-accent rounded-lg p-5 md:p-6">
                <div className="flex items-start">
                  <div className="rounded-full bg-primary/20 p-3 mr-4">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Próxima Sessão</h3>
                    {sessionsLoading ? (
                      <p className="text-gray-600 mt-1 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </p>
                    ) : upcomingSession ? (
                      <>
                        <p className="text-gray-600 mt-1 text-sm">
                          Com {upcomingSession.therapist.name} em <strong>
                            {formatDate(upcomingSession.date)} às {formatTime(upcomingSession.date)}
                          </strong>
                        </p>
                        <div className="mt-2">
                          {upcomingSession.meetingLink && (
                            <a 
                              href={upcomingSession.meetingLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:text-secondary text-sm font-medium inline-block mr-4"
                            >
                              Entrar →
                            </a>
                          )}
                          <Link href="/sessions" className="text-gray-500 hover:text-red-500 text-sm font-medium inline-block">
                            Reagendar
                          </Link>
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-600 mt-1 text-sm">
                        Nenhuma sessão agendada
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-accent rounded-lg p-5 md:p-6">
                <div className="flex items-start">
                  <div className="rounded-full bg-primary/20 p-3 mr-4">
                    <History className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Histórico</h3>
                    <p className="text-gray-600 mt-1 text-sm">
                      {sessionsLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><strong>{completedSessionsCount}</strong> sessões realizadas</>
                      )}
                    </p>
                    <Link href="/sessions" className="text-primary hover:text-secondary text-sm font-medium mt-2 inline-block">
                      Ver histórico →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Therapist Selection Section */}
          <div className="mb-8">
            <div className="flex justify-between items-end mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold">Terapeutas Disponíveis</h2>
                <p className="text-gray-600 mt-1">Escolha o profissional ideal para você</p>
              </div>
              <div>
                <Link href="/therapists">
                  <Button variant="outline" className="ml-2">
                    Ver todos
                  </Button>
                </Link>
              </div>
            </div>
            
            {therapistsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {therapists?.slice(0, 3).map((therapist) => (
                  <TherapistCard key={therapist.id} therapist={therapist} />
                ))}
              </div>
            )}
            
            <div className="mt-6 text-center">
              <Link href="/therapists" className="text-primary hover:text-secondary inline-flex items-center font-medium">
                Ver mais terapeutas 
                <i className="fas fa-chevron-right text-xs ml-1"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
